import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import type { RendererPreference, Theme } from '../../../../game/Config';
import type { BindName, ControlScheme } from '../../../../game/types/Input';
import {
  ALT_SCALE_DEFAULT,
  MINIMAP_SCALE_DEFAULT,
  SCALE_DEFAULT,
  SCALE_MAX,
  SCALE_MIN,
} from '../../../../game/Constants';
import type { FlagCode } from '../../../../generated/flags';
import { UNKNOWN_FLAG, flagSvgPath } from '../../../../services/Flags';
import '../../../airmash-ui/components/range-slider';
import '../../../airmash-ui/components/segmented-control';
import '../../../airmash-ui/components/toggle-switch';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThin } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { gameLogFits } from '../layout';
import './flag-picker';
import { OPEN_GAME_SELECTOR_EVENT } from './game-selector';

/**
 * Menu Drawer - Slide-in menu from the right side
 *
 * Single-layer structure: action items (Keybinds, Invite Friends, Country
 * Flag, Scoreboard, Switch Game), then all settings inline in one scroll,
 * organized by player-intent groups (Camera / Gameplay / Interface /
 * Graphics). The community links and the copyright footer live on the lobby
 * screen, not here. Constant drawer width — nothing widens or overlays, so it
 * stays viable on phone viewports. On mobile it drops the Keybinds row, the
 * key hints, and the settings only a desktop can use. The one page beyond
 * the menu is Country Flag's picker (flag-picker, uiState.menuPage), which
 * takes the drawer's place until Back.
 *
 * Opens when activePanel === 'mainmenu'
 * Closes on backdrop click, ESC key, or menu button toggle
 *
 * @element menu-drawer
 */
@customElement('menu-drawer')
export class MenuDrawer extends LitElement {
  static override styles = [
    tokens,
    scrollbarThin,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body — the drawer owns
           its own typography, and a slow tap on a row or label shouldn't start
           an iOS text selection, callout or grey tap flash. */
        font-family: var(--font-family);
        font-size: var(--font-size-13);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Backdrop overlay */
      .backdrop {
        position: fixed;
        inset: 0;
        background-color: var(--color-bg-backdrop);
        z-index: 100;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition:
          opacity 0.3s ease,
          /* Held until the fade finishes, otherwise visibility flips instantly
             on the way out and the dim vanishes instead of fading. */
          visibility 0s linear 0.3s;
      }

      .backdrop.open {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        /* No delay on the way in — appear at once, then fade up. */
        transition-delay: 0s;
      }

      /* Drawer panel. Constant width — settings live inline in the scroll,
         so nothing can overflow a phone viewport. 92vw cap keeps a backdrop
         sliver on phones for the tap-outside-to-close affordance. */
      .drawer {
        position: fixed;
        top: 0;
        right: 0;
        /* border-box so the safe-area padding-right below stays INSIDE the
           400px/92vw widths — content-box would widen the drawer past the
           cap and eat the phone backdrop sliver. */
        box-sizing: border-box;
        width: 400px;
        max-width: 92vw;
        height: 100%;
        /* Small-viewport height on iOS Safari: track the visible area when
           the URL bar collapses/expands instead of the static layout height. */
        height: 100dvh;
        background-color: rgba(20, 20, 25, 0.95);
        border-left: 1px solid var(--color-border-soft);
        z-index: 101;
        transform: translateX(100%);
        /* Hidden once it's parked off-screen. Left visible there, its shadow
           darkened the right edge of the game view all game. Held until the
           slide finishes, like the backdrop's. */
        visibility: hidden;
        transition:
          transform 0.3s ease,
          visibility 0s linear 0.3s;
        display: flex;
        flex-direction: column;
        box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
        pointer-events: auto;
        /* Right-anchored panel sits against the notch/rounded corner in
           landscape — inset keeps content clear of it. iOS-only in effect. */
        padding-right: env(safe-area-inset-right, 0px);
      }

      .drawer.open {
        transform: translateX(0);
        visibility: visible;
        transition-delay: 0s;
      }

      /* Menu items — the drawer's single scroll area. Top padding clears the
         menu-toolbar's floating X (z-index 150, above the drawer), which
         doubles as the drawer's close button; it adds the status-bar inset
         because the X does. Bottom inset keeps the last row clear of the iOS
         home indicator (no footer below anymore). */
      .menu-items {
        flex: 1;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: calc(55px + env(safe-area-inset-top, 0px)) 0 calc(12px + env(safe-area-inset-bottom, 0px));
      }

      /* On a touch screen the X is a 44px chip from 14px down (menu-toolbar's
         touch layout), so the rows start under it with the chips' own 6px
         gap; 55px put the first row's top 3px beneath the chip. */
      @media (hover: none) {
        .menu-items {
          padding-top: calc(64px + env(safe-area-inset-top, 0px));
        }
      }

      .menu-items[hidden] {
        display: none;
      }

      .menu-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px 24px;
        cursor: pointer;
        transition: background-color 0.2s;
        color: var(--color-text-white);
      }

      @media (hover: hover) {
        .menu-item:hover {
          background-color: var(--color-overlay-subtle);
        }
      }

      .menu-item:active {
        background-color: var(--color-border-subtle);
      }

      .menu-item .icon {
        width: 32px;
        height: 32px;
        opacity: 0.9;
        /* White fill shows through the per-icon SVG mask defined below. */
        background-color: var(--color-text-white);
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .menu-item .label {
        flex: 1;
        font-weight: 500;
      }

      .menu-item .keybind {
        font-size: var(--font-size-sm);
        color: var(--color-text-tertiary);
      }

      /* Current-flag preview on the Country Flag row (sits where other rows
         show their keybind hint). */
      .menu-item .flag-chip {
        width: 24px;
        height: 18px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }

      /* Per-icon mask shape — sizing, positioning and white fill come from
         the shared .menu-item .icon rule above. */
      .icon.keybinds {
        mask-image: url('/vanilla/ui/menu/keyboard.svg');
        -webkit-mask-image: url('/vanilla/ui/menu/keyboard.svg');
      }

      .icon.invite {
        mask-image: url('/vanilla/ui/menu/handshake.svg');
        -webkit-mask-image: url('/vanilla/ui/menu/handshake.svg');
      }

      .icon.scoreboard {
        mask-image: url('/vanilla/ui/menu/trophy.svg');
        -webkit-mask-image: url('/vanilla/ui/menu/trophy.svg');
      }

      .icon.flag {
        mask-image: url('/vanilla/ui/scoreboard/flag.svg');
        -webkit-mask-image: url('/vanilla/ui/scoreboard/flag.svg');
      }

      .icon.switch-game {
        mask-image: url('/vanilla/ui/menu/arrow-left-right.svg');
        -webkit-mask-image: url('/vanilla/ui/menu/arrow-left-right.svg');
      }

      /* Section separator */
      .section-separator {
        height: 1px;
        margin: 8px 24px;
        background: var(--color-overlay-subtle);
      }

      /* Settings — one flat region inside the drawer's scroll, organized by
         small player-intent group labels (Camera / Gameplay / Interface /
         Graphics). Replaces the old Game/Display two-panel taxonomy, which
         neither matched how players look for settings nor fit phone
         viewports (the slide-over widened the drawer to 600px). */
      .settings-region {
        padding: 0 24px 8px;
        color: var(--color-text-secondary);
      }

      /* Group labels carry the structure of the flat list, so they get the
         strongest separation cues: generous space above (proximity groups
         the rows below them), brighter than description text, and a hairline
         rule filling the rest of the line. */
      .settings-group-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: var(--font-size-sm);
        font-weight: bold;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--color-text-muted);
        padding: 30px 0 12px;
      }

      .settings-group-title::after {
        content: '';
        flex: 1;
        height: 1px;
        background: rgba(255, 255, 255, 0.12);
      }

      .settings-group-title:first-child {
        padding-top: 10px;
      }

      /* Settings rows */
      .setting-row {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 20px;
      }

      .setting-label {
        font-size: var(--font-size-sm);
        font-weight: 500;
        color: var(--color-text-white);
        min-height: 20px;
      }

      .setting-description {
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
        margin-top: 4px;
        margin-bottom: 16px;
      }

      /* A <button>: tapping it reloads the page so the change takes effect.
         font: inherit keeps the label's font and weight; native button
         styling would swap in the system font. */
      .restart-badge {
        /* One amber at several strengths. */
        --restart-rgb: 255, 180, 0;
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        background-color: rgba(var(--restart-rgb), 0.2);
        border: 1px solid rgba(var(--restart-rgb), 0.4);
        border-radius: var(--radius-sm);
        font: inherit;
        font-size: var(--font-size-xs);
        color: rgb(var(--restart-rgb));
        margin-left: 8px;
        vertical-align: middle;
        line-height: 1;
        cursor: pointer;
      }

      /* Gated so iOS sticky-hover doesn't leave a tapped badge lit. */
      @media (hover: hover) {
        .restart-badge:hover {
          background-color: rgba(var(--restart-rgb), 0.35);
        }
      }

      .restart-badge:active {
        background-color: rgba(var(--restart-rgb), 0.35);
      }

      /* A taller tap target on touch screens, 37px for the 17px badge. Kept to
         10px so it stays inside the 12px gap above Theme's segmented control. */
      @media (hover: none) {
        .restart-badge::after {
          content: '';
          position: absolute;
          inset: -10px 0;
        }
      }

      .restart-icon {
        width: 1em;
        height: 1em;
        background-color: currentColor;
        mask-image: url('/vanilla/ui/menu/rotate-cw.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/menu/rotate-cw.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      /* Horizontal setting row (label + control side by side). The gap keeps a
         long or wrapping label off its switch. */
      .setting-row-horizontal {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 16px;
      }

      .setting-row-horizontal .setting-label {
        flex: 1;
      }

      /* Sub-settings group (indented children of a parent setting) */
      .sub-settings-group {
        margin-left: 12px;
        padding-left: 12px;
        border-left: 2px solid var(--color-overlay-light);
        margin-bottom: 16px;
      }

      .sub-settings-group .setting-row-horizontal {
        margin-bottom: 12px;
      }

      .sub-settings-group .setting-row-horizontal:last-child {
        margin-bottom: 0;
      }

      .sub-settings-group .setting-label {
        font-size: var(--font-size-xs);
        color: var(--color-text-secondary);
      }

      .sub-settings-group.disabled .setting-label {
        color: var(--color-text-tertiary);
      }

      .attribution {
        /* -10px top pulls the credit up toward the description it belongs to
           (the description's 16px bottom margin would otherwise push it away);
           16px bottom keeps it clear of the unrelated next setting row. */
        margin-top: -10px;
        margin-bottom: 16px;
        font-size: var(--font-size-xxs);
        color: var(--color-text-tertiary);
        opacity: 0.6;
      }

      .attribution a {
        color: var(--color-text-tertiary);
        text-decoration: underline;
      }

      /* Gated so iOS sticky-hover doesn't leave a tapped credit lit. */
      @media (hover: hover) {
        .attribution a:hover {
          color: var(--color-text-secondary);
        }
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** Initial renderer preference (to detect if restart is needed) */
  private initialRendererPref: RendererPreference | null = null;

  /** Initial theme (to detect if restart is needed) */
  private initialTheme: Theme | null = null;

  /** Initial HiDPI setting (to detect if restart is needed) */
  private initialHidpi: boolean | null = null;

  /** Whether the settings are in the DOM. Set on open and cleared once the
   *  close slide ends, so the drawer keeps its content and scroll position
   *  while it slides out, rather than emptying and jumping to the top. The
   *  flag page follows it too. */
  @state() private settingsMounted = false;

  override connectedCallback(): void {
    super.connectedCallback();
    // ESC closes the menu. Capture phase so the open drawer owns the close
    // before game-side handlers — but after context-menu's WINDOW capture
    // listener, which by event order still wins when both are open.
    document.addEventListener('keydown', this.handleKeyDown, { capture: true });
  }

  override firstUpdated(): void {
    // The renderer, theme and HiDPI the running game was built with, for the
    // restart badges: the mirror holds the saved values from the boot's sync
    // on, and all three are this device's, so no account copy can replace
    // them later.
    this.initialRendererPref = this.uiState.displaySettings.rendererPreference;
    this.initialTheme = this.uiState.displaySettings.theme;
    this.initialHidpi = this.uiState.displaySettings.hidpi;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown, { capture: true });
  }

  protected override willUpdate(): void {
    if (this.uiState.activePanel === 'mainmenu') {
      this.settingsMounted = true;
    } else if (this.uiState.uiHidden) {
      // Closed while F4 hides the interface: the game screen is display none,
      // so the drawer has no box and no slide to end, and the settings would
      // stay until the next open and close
      this.settingsMounted = false;
    }
  }

  /** The close slide has finished, or was cut short (F4 mid-slide): drop the
   *  settings, so the closed drawer costs nothing to update and reopens at
   *  the top. Row hover transitions bubble up here too, hence the filter. */
  private handleDrawerTransitionEnd = (e: TransitionEvent): void => {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
    if (this.uiState.activePanel !== 'mainmenu') this.settingsMounted = false;
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || this.uiState.activePanel !== 'mainmenu') return;
    // Consume — the drawer owns this ESC; without this the same event also
    // reaches the game-side handler (whose closeAllPanels was redundant here),
    // and layered closes (e.g. radio open behind the menu) collapse at once.
    e.preventDefault();
    e.stopPropagation();
    // The flag page steps back to the menu; the menu itself closes.
    if (this.uiState.menuPage === 'flag') {
      this.gameApi.showMenuPage('main');
    } else {
      this.gameApi.closePanel();
    }
  };

  /** Tapping outside closes the drawer, silently like every outside dismissal
   *  (the click rule). The menu button's X, a button, does click. */
  private handleBackdropClick = (e: Event): void => {
    e.stopPropagation();
    this.gameApi.closePanel();
  };

  /** A menu row's click handler: keep the click from the game, play the tap
   *  sound, run the action. Called once per field, so each row keeps one
   *  stable listener instead of Lit re-binding a fresh closure every render. */
  private onRowTap(action: () => void) {
    return (e: Event): void => {
      e.stopPropagation();
      this.gameApi.playClick();
      action();
    };
  }

  /** A toggle-switch change handler that clicks, then applies the new state.
   *  Built once per field, like onRowTap. */
  private onToggle(apply: (checked: boolean) => void) {
    return (e: CustomEvent<{ checked: boolean }>): void => {
      e.stopPropagation();
      this.gameApi.playClick();
      apply(e.detail.checked);
    };
  }

  /** A segmented-control change handler. The event carries a plain string, so
   *  it's looked up in the control's own typed options: that narrows it
   *  without a cast, and ignores a value the control doesn't offer. */
  private onChoice<T extends string>(options: readonly { value: T }[], apply: (value: T) => void) {
    return (e: CustomEvent<{ value: string }>): void => {
      e.stopPropagation();
      const option = options.find((o) => o.value === e.detail.value);
      if (!option) return;
      this.gameApi.playClick();
      apply(option.value);
    };
  }

  private handleKeybinds = this.onRowTap(() => this.gameApi.showPanel('keybinds'));
  private handleInvite = this.onRowTap(() => this.gameApi.showPanel('invite'));
  private handleFlag = this.onRowTap(() => this.gameApi.showMenuPage('flag'));
  private handleScoreboard = this.onRowTap(() => this.gameApi.showScorePanel());

  /** Opens the game list, the one F2 and the logo open: it switches games in
   *  the region without a reload, and its CHANGE REGION goes to the lobby. */
  private handleSwitchGame = this.onRowTap(() => {
    this.gameApi.closePanel();
    window.dispatchEvent(new Event(OPEN_GAME_SELECTOR_EVENT));
  });

  /** Current flag preview for the Country Flag row. From the uiState mirror,
   *  so it changes in place when the server confirms a pick made on the flag
   *  page, with the menu still open. Hidden while the flag is still the 'xx'
   *  unknown default. */
  private renderCurrentFlagChip(current: FlagCode | null) {
    return current
      ? html`<span class="flag-chip" style="background-image: url('${flagSvgPath(current)}')"></span>`
      : nothing;
  }

  /** Reactive shortcut hint for a menu row, e.g. "(K)" — tracks user remaps
   *  via the live keybind config. Renders nothing when the action is unbound,
   *  or on mobile, where there's no keyboard to press it on. */
  private keyHint(bindName: BindName) {
    if (this.uiState.isMobile) return nothing;
    const key = this.uiState.keybinds.bindByName[bindName];
    return key ? html`<span class="keybind">(${key})</span>` : nothing;
  }

  /** Sprite theme options for segmented control */
  private readonly themeOptions: { value: Theme; label: string }[] = [
    { value: 'default', label: 'Default' },
    { value: 'hitshapes', label: 'Hit Circles' },
  ];

  /** Touch control scheme options for segmented control (mobile only) */
  private readonly controlSchemeOptions: { value: ControlScheme; label: string }[] = [
    { value: 'pointToAim', label: 'Point-to-aim' },
    { value: 'directional', label: 'Directional' },
  ];

  // Declared after the options above, which they read as they're built.
  private handleThemeChange = this.onChoice(this.themeOptions, (theme) =>
    this.gameApi.setDisplaySetting('theme', theme),
  );
  private handleControlSchemeChange = this.onChoice(this.controlSchemeOptions, (scheme) =>
    this.gameApi.setGameSetting('controlScheme', scheme),
  );

  /** The restart badge's tap. The reload that follows may cut the click short. */
  private handleRestart = this.onRowTap(() => this.gameApi.reload());

  private handleRendererChange = this.onToggle((on) =>
    this.gameApi.setDisplaySetting('rendererPreference', on ? 'webgpu' : 'webgl'),
  );
  private handleHidpiChange = this.onToggle((on) => this.gameApi.setDisplaySetting('hidpi', on));
  private handleSOCDModeChange = this.onToggle((on) =>
    this.gameApi.setGameSetting('socdMode', on ? 'lastInput' : 'off'),
  );
  private handleMouseModeChange = this.onToggle((on) => this.gameApi.setMouseMode(on));
  private handleOutlinesChange = this.onToggle((on) => this.gameApi.setDisplaySetting('outlines', on));
  private handleGridChange = this.onToggle((on) => this.gameApi.setDisplaySetting('grid', on));
  private handleMountainTintChange = this.onToggle((on) => this.gameApi.setDisplaySetting('mountainTint', on));
  private handleMapFillChange = this.onToggle((on) => this.gameApi.setDisplaySetting('mapFill', on));
  private handleIgnoreBotsChange = this.onToggle((on) => this.gameApi.setGameSetting('ignoreBots', on));
  private handleBotsColorChange = this.onToggle((on) => this.gameApi.setGameSetting('botsColor', on));
  private handleLaserPointerChange = this.onToggle((on) => this.gameApi.setGameSetting('laserPointer', on));
  private handlePreciseAimChange = this.onToggle((on) => this.gameApi.setGameSetting('preciseAim', on));
  private handleProwlerAlertChange = this.onToggle((on) => this.gameApi.setGameSetting('prowlerAlert', on));
  private handleVoiceEventsCtfChange = this.onToggle((on) => this.gameApi.setGameSetting('voiceEventsCtf', on));
  private handleCtfSpawnLinesChange = this.onToggle((on) => this.gameApi.setGameSetting('ctfSpawnLines', on));
  private handleExtendMapEdgesChange = this.onToggle((on) => this.gameApi.setGameSetting('extendMapEdges', on));
  private handleSquaredSceneChange = this.onToggle((on) => this.gameApi.setGameSetting('squaredScene', on));
  private handleExtendedNameplatesChange = this.onToggle((on) => this.gameApi.setGameSetting('extendedNameplates', on));
  private handleNameplateRankChange = this.onToggle((on) => this.gameApi.setGameSetting('nameplateRank', on));
  private handleTeammateHealthChange = this.onToggle((on) => this.gameApi.setGameSetting('teammateHealth', on));
  private handleScaleNameplatesChange = this.onToggle((on) => this.gameApi.setGameSetting('scaleNameplates', on));
  private handleGameLogChange = this.onToggle((on) => this.gameApi.setGameSetting('gameLog', on));
  private handleGameLogKillsChange = this.onToggle((on) => this.gameApi.setGameSetting('gameLogKills', on));
  private handleGameLogJoinLeaveChange = this.onToggle((on) => this.gameApi.setGameSetting('gameLogJoinLeave', on));
  private handleGameLogCtfChange = this.onToggle((on) => this.gameApi.setGameSetting('gameLogCtf', on));
  private handleGameLogBtrChange = this.onToggle((on) => this.gameApi.setGameSetting('gameLogBtr', on));
  private handleShowCommandHintsChange = this.onToggle((on) => this.gameApi.setGameSetting('showCommandHints', on));

  /** Minimap slider drag: resize live, save nothing. */
  private handleMinimapScaleInput = (e: CustomEvent<{ value: number }>): void => {
    e.stopPropagation();
    this.gameApi.applyMinimapScale(e.detail.value);
  };

  /** Minimap slider release (or reset): resize and save. */
  private handleMinimapScaleChange = (e: CustomEvent<{ value: number }>): void => {
    e.stopPropagation();
    this.gameApi.setMinimapScale(e.detail.value);
  };

  private handleScalingFactorInput = (e: CustomEvent<{ value: number }>): void => {
    e.stopPropagation();
    if (this.gameApi.getScalingAltMode()) {
      this.gameApi.toggleScalingAltMode();
    }
    this.gameApi.applyScalingFactor(e.detail.value);
  };

  private handleScalingFactorChange = (e: CustomEvent<{ value: number }>): void => {
    e.stopPropagation();
    this.gameApi.setScalingFactor(e.detail.value);
  };

  private handleAltScalingFactorInput = (e: CustomEvent<{ value: number }>): void => {
    e.stopPropagation();
    if (!this.gameApi.getScalingAltMode()) {
      this.gameApi.toggleScalingAltMode();
    }
    this.gameApi.applyAltScalingFactor(e.detail.value);
  };

  private handleAltScalingFactorChange = (e: CustomEvent<{ value: number }>): void => {
    e.stopPropagation();
    this.gameApi.setAltScalingFactor(e.detail.value);
  };

  /** Gameplay group — input behavior and play aids. */
  private renderGameplaySettings() {
    return html`
      ${
        this.uiState.isMobile
          ? html`
              <!-- Control under the label, like Theme: two segments beside
                   the label squeezed it flush against them on a phone. -->
              <div class="setting-row">
                <div class="setting-label">Touch controls</div>
                <segmented-control
                  .options=${this.controlSchemeOptions}
                  .value=${this.uiState.gameSettings.controlScheme}
                  @change=${this.handleControlSchemeChange}
                ></segmented-control>
                <div class="setting-description">
                  Point-to-aim: drag toward where you want to fly. Directional: keyboard-style steering, with reverse
                  when you pull back.
                </div>
              </div>
              ${
                this.uiState.gameSettings.controlScheme === 'pointToAim'
                  ? html`
                      <div class="setting-row-horizontal">
                        <div class="setting-label">Precise aim</div>
                        <toggle-switch
                          .checked=${this.uiState.gameSettings.preciseAim}
                          @change=${this.handlePreciseAimChange}
                        ></toggle-switch>
                      </div>
                      <div class="setting-description">
                        Steers to your finger's exact heading — the ship lands on target instead of stopping a little
                        short, and direction changes react instantly. Off is the classic pulse steering.
                      </div>
                    `
                  : nothing
              }
            `
          : nothing
      }
      <!-- Mouse mode and last-input SOCD only run in the mouse and keyboard
           handlers (touch and gamepad input take another path), so on mobile
           the toggles would do nothing — the Keybinds row's call. -->
      ${
        this.uiState.isMobile
          ? nothing
          : html`
              <div class="setting-row-horizontal">
                <div class="setting-label">Mouse mode</div>
                <toggle-switch
                  .checked=${this.uiState.gameSettings.mouseMode}
                  @change=${this.handleMouseModeChange}
                ></toggle-switch>
              </div>
              <div class="setting-description">
                Left click fires and right click uses your special, while you steer with WASD or the
                arrows.${
                  this.uiState.keybinds.bindByName['MOUSEMODE']
                    ? ` Press ${this.uiState.keybinds.bindByName['MOUSEMODE']} during gameplay to toggle it.`
                    : ''
                }
              </div>

              <div class="setting-row-horizontal">
                <div class="setting-label">Instant turn reversal</div>
                <toggle-switch
                  .checked=${this.uiState.gameSettings.socdMode === 'lastInput'}
                  @change=${this.handleSOCDModeChange}
                ></toggle-switch>
              </div>
              <div class="setting-description">
                While holding one direction, pressing the opposite one turns you immediately — normally the two keys
                cancel and you fly straight until one is released. Known as SOCD last-input.
              </div>
              <div class="attribution">
                Inspired by musings with
                <a href="https://github.com/wight-airmash" target="_blank" rel="noopener">wight</a>
              </div>
            `
      }

      <div class="setting-row-horizontal">
        <div class="setting-label">Laser pointer</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.laserPointer}
          @change=${this.handleLaserPointerChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">Draw an aiming line showing exactly where your ship is pointing.</div>
      <div class="attribution">
        Inspired by
        <a href="https://github.com/fabiospampinato/airmash-swam-extensions" target="_blank" rel="noopener"
          >Laser Pointer</a
        >
        by Fabio Spampinato
      </div>

      <div class="setting-row-horizontal">
        <div class="setting-label">CTF voice events</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.voiceEventsCtf}
          @change=${this.handleVoiceEventsCtfChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">Voice commentary on flag taken / captured / returned.</div>
    `;
  }

  /** Interface group — HUD, nameplates, log, and on-screen info. */
  private renderInterfaceSettings() {
    return html`
      <div class="setting-row-horizontal">
        <div class="setting-label">Prowler alert</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.prowlerAlert}
          @change=${this.handleProwlerAlertChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">Show a warning when a cloaked enemy Prowler is sneaking up on you.</div>

      <div class="setting-row-horizontal">
        <div class="setting-label">CTF spawn lines</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.ctfSpawnLines}
          @change=${this.handleCtfSpawnLinesChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">
        Mark the team spawn boundaries on the map in CTF — blue and red lines showing where each side respawns.
      </div>
      <div class="attribution">
        Inspired by
        <a href="https://github.com/starmash-ext/starmash-things" target="_blank" rel="noopener">De</a>
      </div>

      <div class="setting-row-horizontal">
        <div class="setting-label">Hide bots</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.ignoreBots}
          @change=${this.handleIgnoreBotsChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">
        Hide bot players from the leaderboard and scoreboard, and bot-only lines from the game log.
      </div>

      <div class="setting-row-horizontal">
        <div class="setting-label">Distinguish bots on minimap</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.botsColor}
          @change=${this.handleBotsColorChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">Draw bot dots smaller so humans stand out.</div>

      <div class="setting-row-horizontal">
        <div class="setting-label">Extended nameplates</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.extendedNameplates}
          @change=${this.handleExtendedNameplatesChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">Show extra info on player names.</div>

      <div class="sub-settings-group ${this.uiState.gameSettings.extendedNameplates ? '' : 'disabled'}">
        <div class="setting-row-horizontal">
          <div class="setting-label">Leaderboard rank</div>
          <toggle-switch
            .checked=${this.uiState.gameSettings.nameplateRank}
            .disabled=${!this.uiState.gameSettings.extendedNameplates}
            @change=${this.handleNameplateRankChange}
          ></toggle-switch>
        </div>

        <div class="setting-row-horizontal">
          <div class="setting-label">Teammate health</div>
          <toggle-switch
            .checked=${this.uiState.gameSettings.teammateHealth}
            .disabled=${!this.uiState.gameSettings.extendedNameplates}
            @change=${this.handleTeammateHealthChange}
          ></toggle-switch>
        </div>
      </div>

      <div class="setting-row-horizontal">
        <div class="setting-label">Scale nameplates with zoom</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.scaleNameplates}
          @change=${this.handleScaleNameplatesChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">
        Resize player names with the zoom level so they stay readable when zoomed out.
      </div>

      ${
        !gameLogFits(this.uiState)
          ? nothing
          : html`
              <div class="setting-row-horizontal">
                <div class="setting-label">Game log</div>
                <toggle-switch
                  .checked=${this.uiState.gameSettings.gameLog}
                  @change=${this.handleGameLogChange}
                ></toggle-switch>
              </div>
              <div class="setting-description">Show a live feed of game events in the top-left corner.</div>

              <div class="sub-settings-group ${this.uiState.gameSettings.gameLog ? '' : 'disabled'}">
                <div class="setting-row-horizontal">
                  <div class="setting-label">Kills</div>
                  <toggle-switch
                    .checked=${this.uiState.gameSettings.gameLogKills}
                    .disabled=${!this.uiState.gameSettings.gameLog}
                    @change=${this.handleGameLogKillsChange}
                  ></toggle-switch>
                </div>

                <div class="setting-row-horizontal">
                  <div class="setting-label">Join / leave</div>
                  <toggle-switch
                    .checked=${this.uiState.gameSettings.gameLogJoinLeave}
                    .disabled=${!this.uiState.gameSettings.gameLog}
                    @change=${this.handleGameLogJoinLeaveChange}
                  ></toggle-switch>
                </div>

                <div class="setting-row-horizontal">
                  <div class="setting-label">CTF events</div>
                  <toggle-switch
                    .checked=${this.uiState.gameSettings.gameLogCtf}
                    .disabled=${!this.uiState.gameSettings.gameLog}
                    @change=${this.handleGameLogCtfChange}
                  ></toggle-switch>
                </div>

                <div class="setting-row-horizontal">
                  <div class="setting-label">BTR events</div>
                  <toggle-switch
                    .checked=${this.uiState.gameSettings.gameLogBtr}
                    .disabled=${!this.uiState.gameSettings.gameLog}
                    @change=${this.handleGameLogBtrChange}
                  ></toggle-switch>
                </div>
              </div>
            `
      }

      <!-- Command hints belong to the desktop chat box; mobile chat has none. -->
      ${
        this.uiState.isMobile
          ? nothing
          : html`
              <div class="setting-row-horizontal">
                <div class="setting-label">Command hints</div>
                <toggle-switch
                  .checked=${this.uiState.gameSettings.showCommandHints}
                  @change=${this.handleShowCommandHintsChange}
                ></toggle-switch>
              </div>
              <div class="setting-description">Show help for / commands above the chat box as you type.</div>
            `
      }
    `;
  }

  /** Camera group — zoom, minimap, and view-edge behavior. */
  private renderCameraSettings() {
    const minimapLimits = this.gameApi.getMinimapScaleLimits();
    const { isMobile } = this.uiState;
    const { scalingAltMode } = this.uiState.zoom;
    // The alternate zoom exists for its mid-game keyboard toggle (the ALTZOOM
    // bind), which touch has no way to, so a touch device's menu hides it
    // rather than offer a dead-end second zoom; unless it is the active one,
    // which a tablet's keyboard or the account's settings can make it, and
    // then the slider on screen must be the zoom on screen. Only with both
    // rows does the main one need "(Active)", or calling "main".
    const showAlt = !isMobile || scalingAltMode;

    return html`
      <div class="setting-row">
        <div class="setting-label">Zoom level ${showAlt && !scalingAltMode ? '(Active)' : nothing}</div>
        <range-slider
          .value=${this.uiState.zoom.scalingFactor}
          .min=${SCALE_MIN}
          .max=${SCALE_MAX}
          .step=${100}
          .defaultValue=${SCALE_DEFAULT}
          @input=${this.handleScalingFactorInput}
          @change=${this.handleScalingFactorChange}
        ></range-slider>
        <div class="setting-description">
          ${showAlt ? 'Main zoom level. ' : nothing}Lower values zoom in, higher values zoom out.
          ${this.uiState.zoom.serverCap === null ? nothing : ` This server allows up to ${this.uiState.zoom.serverCap}.`}
        </div>
      </div>

      <!-- A touch toggle for the alternate zoom would belong to the
           touch-controls rework. -->
      ${
        showAlt
          ? html`
              <div class="setting-row">
                <div class="setting-label">Alternate zoom level ${scalingAltMode ? '(Active)' : nothing}</div>
                <range-slider
                  .value=${this.uiState.zoom.altScalingFactor}
                  .min=${SCALE_MIN}
                  .max=${SCALE_MAX}
                  .step=${100}
                  .defaultValue=${ALT_SCALE_DEFAULT}
                  @input=${this.handleAltScalingFactorInput}
                  @change=${this.handleAltScalingFactorChange}
                ></range-slider>
                <div class="setting-description">
                  Alternate zoom
                  level.${
                    this.uiState.keybinds.bindByName['ALTZOOM']
                      ? ` Press ${this.uiState.keybinds.bindByName['ALTZOOM']} during gameplay to toggle between zoom levels.`
                      : ' Toggle between zoom levels during gameplay.'
                  }
                </div>
              </div>
            `
          : nothing
      }

      <div class="setting-row">
        <div class="setting-label">Minimap size</div>
        <range-slider
          .value=${this.uiState.displaySettings.minimapScale}
          .min=${minimapLimits.min}
          .max=${minimapLimits.max}
          .step=${0.25}
          .defaultValue=${MINIMAP_SCALE_DEFAULT}
          .format=${'{value}x'}
          @input=${this.handleMinimapScaleInput}
          @change=${this.handleMinimapScaleChange}
        ></range-slider>
        <div class="setting-description">
          Adjust the minimap
          size.${isMobile ? nothing : ' Scroll the mouse wheel over the minimap to resize it, or double-click it to reset.'}
        </div>
      </div>

      <div class="setting-row-horizontal">
        <div class="setting-label">Extend map edges</div>
        <toggle-switch
          .checked=${this.uiState.gameSettings.extendMapEdges}
          @change=${this.handleExtendMapEdgesChange}
        ></toggle-switch>
      </div>
      <div class="setting-description">
        Keep your plane centered at the edges of the map, instead of the camera stopping at the border.
      </div>
      <div class="attribution">
        Inspired by
        <a href="https://github.com/starmash-ext/starmash-things" target="_blank" rel="noopener">De</a>
      </div>

      ${
        isMobile
          ? nothing
          : html`
              <div class="setting-row-horizontal">
                <div class="setting-label">Squared game area</div>
                <toggle-switch
                  .checked=${this.uiState.gameSettings.squaredScene}
                  @change=${this.handleSquaredSceneChange}
                ></toggle-switch>
              </div>
              <div class="setting-description">
                Cap the game view at an almost-square, centered on screen — equal sight range in every direction, so
                zoom behaves the same horizontally and vertically. The UI keeps the full window.
              </div>
            `
      }
    `;
  }

  /** Badge beside a setting that only applies at startup, shown once it's
   *  changed. Tapping it reloads the page to apply the change. */
  private renderRestartBadge() {
    return html`
      <button type="button" class="restart-badge" @click=${this.handleRestart}>
        <span class="restart-icon"></span>Restart to apply
      </button>
    `;
  }

  /** Graphics group — the theme with its Hit Circles options, and the
   *  rendering engine. */
  private renderGraphicsSettings() {
    const currentPref = this.uiState.displaySettings.rendererPreference;
    const needsRestart = this.initialRendererPref !== null && currentPref !== this.initialRendererPref;
    const currentTheme = this.uiState.displaySettings.theme;
    const themeNeedsRestart = this.initialTheme !== null && currentTheme !== this.initialTheme;
    const hidpi = this.uiState.displaySettings.hidpi;
    const hidpiNeedsRestart = this.initialHidpi !== null && hidpi !== this.initialHidpi;
    const activeRenderer = this.gameApi.getActiveRendererType();
    const activeLabel = activeRenderer === 'webgpu' ? 'WebGPU' : activeRenderer === 'webgl' ? 'WebGL' : 'Unknown';

    return html`
      <div class="setting-row">
        <div class="setting-label">Theme ${themeNeedsRestart ? this.renderRestartBadge() : nothing}</div>
        <segmented-control
          .options=${this.themeOptions}
          .value=${currentTheme}
          @change=${this.handleThemeChange}
        ></segmented-control>
        <div class="setting-description">
          Game graphics mode. Hit Circles replaces the artwork with the true collision shapes the server uses.
        </div>
      </div>

      ${
        currentTheme === 'hitshapes'
          ? html`
              <div class="sub-settings-group">
                <div class="setting-row-horizontal">
                  <div class="setting-label">Map outlines</div>
                  <toggle-switch
                    .checked=${this.uiState.displaySettings.outlines}
                    @change=${this.handleOutlinesChange}
                  ></toggle-switch>
                </div>
                <div class="setting-row-horizontal">
                  <div class="setting-label">Background grid</div>
                  <toggle-switch
                    .checked=${this.uiState.displaySettings.grid}
                    @change=${this.handleGridChange}
                  ></toggle-switch>
                </div>
                <div class="setting-row-horizontal">
                  <div class="setting-label">Terrain tint</div>
                  <toggle-switch
                    .checked=${this.uiState.displaySettings.mountainTint}
                    @change=${this.handleMountainTintChange}
                  ></toggle-switch>
                </div>
                <div class="setting-row-horizontal">
                  <div class="setting-label">Land fill</div>
                  <toggle-switch
                    .checked=${this.uiState.displaySettings.mapFill}
                    @change=${this.handleMapFillChange}
                  ></toggle-switch>
                </div>
                <div class="attribution">
                  Inspired by
                  <a href="https://github.com/fabiospampinato/airmash-swam-extensions" target="_blank" rel="noopener"
                    >Hit Circles</a
                  >
                  by Fabio Spampinato
                </div>
              </div>
            `
          : nothing
      }

      <div class="setting-row-horizontal">
        <div class="setting-label">Prefer WebGPU ${needsRestart ? this.renderRestartBadge() : nothing}</div>
        <toggle-switch .checked=${currentPref === 'webgpu'} @change=${this.handleRendererChange}></toggle-switch>
      </div>
      <div class="setting-description">
        Use the modern graphics engine where the browser supports it, automatically falling back to WebGL where it
        doesn't. Turn off to force WebGL if you see rendering problems.<br />
        Currently using: <strong>${activeLabel}</strong>
      </div>

      <div class="setting-row-horizontal">
        <div class="setting-label">HiDPI graphics ${hidpiNeedsRestart ? this.renderRestartBadge() : nothing}</div>
        <toggle-switch .checked=${hidpi} @change=${this.handleHidpiChange}></toggle-switch>
      </div>
      <div class="setting-description">
        Render the game at your screen's full pixel density: sharper on phones and high-resolution displays, at a cost
        in frame rate on weaker hardware.
      </div>
    `;
  }

  override render() {
    const isOpen = this.uiState.activePanel === 'mainmenu';
    // Open, or sliding shut, on the flag page (see settingsMounted)
    const onFlagPage = this.settingsMounted && this.uiState.menuPage === 'flag';
    const currentFlag = this.uiState.myFlag === UNKNOWN_FLAG ? null : this.uiState.myFlag;

    return html`
      <div class="backdrop ${isOpen ? 'open' : ''}" @click=${this.handleBackdropClick}></div>

      <div
        class="drawer ${isOpen ? 'open' : ''}"
        @transitionend=${this.handleDrawerTransitionEnd}
        @transitioncancel=${this.handleDrawerTransitionEnd}
      >
        ${
          onFlagPage
            ? html`<flag-picker .current=${currentFlag} .focusSearch=${!this.uiState.isMobile}></flag-picker>`
            : nothing
        }
        <!-- Hidden rather than unmounted under the flag page, so Back
             returns to the same scroll position. -->
        <div class="menu-items scrollbar-thin" ?hidden=${onFlagPage}>
          <!-- Keybinds remap physical keyboard keys — meaningless on touch
               devices (and the panel itself is desktop-sized). The K bind is
               refused on mobile too (Input.ts toggleKeybinds), so a tablet
               with a keyboard has no way to the panel either. -->
          ${
            this.uiState.isMobile
              ? nothing
              : html`
                  <div class="menu-item" @click=${this.handleKeybinds}>
                    <div class="icon keybinds"></div>
                    <span class="label">Keybinds</span>
                    ${this.keyHint('KEYBINDS')}
                  </div>
                `
          }

          <div class="menu-item" @click=${this.handleInvite}>
            <div class="icon invite"></div>
            <span class="label">Invite Friends</span>
            ${this.keyHint('INVITE')}
          </div>

          <!-- Country flag = player identity. Desktop sets it via /flag in
               chat; this row is the tap path (the flag page), added mainly
               for mobile but universal. -->
          <div class="menu-item" @click=${this.handleFlag}>
            <div class="icon flag"></div>
            <span class="label">Country Flag</span>
            ${this.renderCurrentFlagChip(currentFlag)}
          </div>

          <div class="menu-item" @click=${this.handleScoreboard}>
            <div class="icon scoreboard"></div>
            <span class="label">Scoreboard</span>
            ${this.keyHint('SHOWSCORE')}
          </div>

          <div class="menu-item" @click=${this.handleSwitchGame}>
            <div class="icon switch-game"></div>
            <span class="label">Switch Game</span>
            ${this.keyHint('SHOWGAMES')}
          </div>

          <div class="section-separator"></div>

          <!-- Settings — one flat region, grouped by player intent. Mounted
               from open until the close slide ends (settingsMounted), so the
               closed, off-screen drawer costs nothing to update. -->
          ${
            this.settingsMounted
              ? html`
                  <div class="settings-region">
                    <div class="settings-group-title">Camera</div>
                    ${this.renderCameraSettings()}
                    <div class="settings-group-title">Gameplay</div>
                    ${this.renderGameplaySettings()}
                    <div class="settings-group-title">Interface</div>
                    ${this.renderInterfaceSettings()}
                    <div class="settings-group-title">Graphics</div>
                    ${this.renderGraphicsSettings()}
                  </div>
                `
              : nothing
          }
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'menu-drawer': MenuDrawer;
  }
}
